import {
	DropdownMenuPortal,
	Root as DropdownMenuRoot,
	DropdownMenuTrigger
} from '@radix-ui/react-dropdown-menu'
import { useQuery } from '@tanstack/react-query'
import {
	DropdownContent,
	DropdownItem,
	DropdownSeparator
} from '@/components/ui/dropdown-atoms'
import {
	BunIcon,
	CheckIcon,
	DotsHorizontalIcon,
	EditAirbnbIcon,
	MailIcon
} from '@/components/ui/icons'
import { Tooltip } from '@/components/ui/tooltip'
import { operatorQueryOptions } from '@/features/marquee/query-options'
import { type TeamRowAction, teamRowActions } from '@/features/marquee/utils'
import { cn } from '@/helpers/utils'
import type { MarqueeManagedOperator } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

/**
 * The shared `DropdownItem` keeps its own type, padding and hover; all this adds
 * is the row shape the design asks for: label left, icon hard right.
 */
const ITEM_CLASS = 'flex items-center justify-between gap-4'

interface TeamMemberRowMenuProps {
	member: MarqueeManagedOperator
	onAction: (action: TeamRowAction, member: MarqueeManagedOperator) => void
}

/**
 * The Team row's kebab. What it offers follows the row's status
 * ([[teamRowActions]]): edit, then resend for an invite nobody accepted yet,
 * deactivate for an active person and reactivate for a deactivated one.
 *
 * An admin row has no menu, the viewer's own included: the API refuses every
 * write on admins, so the row says who manages them instead (dev note on the
 * design).
 *
 * Every item hands the row back to the page, which owns the dialogs: the menu
 * unmounts its content on select, so a dialog kept in here would be torn down
 * by the click that opens it, and the edit form opens the same deactivate.
 */
export function TeamMemberRowMenu({
	member,
	onAction
}: TeamMemberRowMenuProps) {
	const { t } = useTranslation()
	const { data: operator } = useQuery(operatorQueryOptions())
	const actions = teamRowActions(member, operator)
	const trigger = (
		<button
			aria-label={t('marquee:team_row_menu_label')}
			className='flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-neutral-500 transition hover:bg-grey-100 hover:text-neutral-900 data-[state=open]:bg-grey-100 data-[state=open]:text-neutral-900'
			type='button'
		>
			<DotsHorizontalIcon className='rotate-90' size={20} />
		</button>
	)
	if (member.role === 'admin') {
		return (
			<Tooltip
				content={t('marquee:team_self_row_tooltip')}
				maxWidth={320}
				triggerProps={{
					'aria-label': t('marquee:team_self_row_tooltip'),
					asChild: true
				}}
			>
				<div>{trigger}</div>
			</Tooltip>
		)
	}

	if (actions.length === 0) {
		return null
	}

	const showSeparator =
		actions.includes('edit') &&
		(actions.includes('deactivate') || actions.includes('reactivate'))

	return (
		<DropdownMenuRoot>
			<DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownContent align='end' sideOffset={4}>
					{actions.includes('edit') && (
						<DropdownItem
							className={ITEM_CLASS}
							onClick={() => onAction('edit', member)}
						>
							{t('marquee:team_action_edit')}
							<EditAirbnbIcon size={20} />
						</DropdownItem>
					)}
					{actions.includes('resend_invite') && (
						<DropdownItem
							className={ITEM_CLASS}
							onClick={() => onAction('resend_invite', member)}
						>
							{t('marquee:team_action_resend_invite')}
							<MailIcon size={20} />
						</DropdownItem>
					)}
					{showSeparator && <DropdownSeparator />}
					{actions.includes('deactivate') && (
						<DropdownItem
							className={cn(ITEM_CLASS, 'text-red-700')}
							onClick={() => onAction('deactivate', member)}
						>
							{t('marquee:team_action_deactivate')}
							<BunIcon size={20} />
						</DropdownItem>
					)}
					{actions.includes('reactivate') && (
						<DropdownItem
							className={cn(ITEM_CLASS, 'text-green-700')}
							onClick={() => onAction('reactivate', member)}
						>
							{t('marquee:team_action_reactivate')}
							<CheckIcon size={20} />
						</DropdownItem>
					)}
				</DropdownContent>
			</DropdownMenuPortal>
		</DropdownMenuRoot>
	)
}
